import React, { useState, useEffect } from 'react';
import { FiUploadCloud, FiSave, FiEdit, FiTrash2 } from 'react-icons/fi';
import { useFormik } from 'formik';
import useSublimationClothPrintConfig from '../../../API/Hooks/useSublimationClothPrintConfig';
import useToast from '../../../API/GlobalHooks/useToast';
import { useModal } from '../../ConfirmationModal/ModalContext';
import { handleDelete_data, handleGet_data, handleSubmit_data } from './clothTypeModel';
import { getImageUrl } from '../../../MIS/Global';

const ClothType = () => {
  const showToast = useToast();
  const { showModal } = useModal();
  const { validationSchema, initialValues } = useSublimationClothPrintConfig();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [actionLoader, setActionLoader] = useState([]);
  const [files, setFiles] = useState(null);
  const [editIndex, setEditIndex] = useState(null);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      const data = {
        print_type_name: values.print_type_name,        
        ...((values?.id === 0 || files) && { imgpath: files }),
        ...(values?.id > 0 && { id: values.id })
      };

      handleSubmit_data({
        setLoading,
        data,
        showToast,
        resetForm,
        setListLoading,
        setItems,setEditIndex,setFiles
      });

      document.getElementById('clothImageInput').value = '';
    },
  });

  useEffect(() => {
    handleGet_data({ setListLoading, showToast, setItems,setEditIndex });
  }, []);

  const handleImageChange = (e) => {
    const file = e.currentTarget.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      formik.setFieldValue('imgpath', reader.result);
    };
    reader.readAsDataURL(file);
    setFiles(file);
  };

  const handleEdit = (index) => {
    const item = items[index];
    formik.setValues({
      id:item?.id,
      print_type_name: item?.print_type_name,
      imgpath: getImageUrl(item.imgpath),
    });
    setEditIndex(index);
  };
  
  return (
    <div className="container-fluid px-2">
      <div className="row g-3">
        {/* LEFT SIDE */}
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-header py-2">
              <h6 className="mb-0">Add / Edit Cloth Print Type</h6>
            </div>
            <div className="card-body p-3">
              <form onSubmit={formik.handleSubmit} autoComplete='off'>
                {/* Upload Box */}
                <div className="mb-3">
                  <label className="form-label small">Upload Image</label>
                  <div
                    className={`border rounded d-flex justify-content-center align-items-center ${
                      formik.touched.imgpath && formik.errors.imgpath ? 'border-danger' : ''
                    }`}
                    onClick={() => document.getElementById('clothImageInput').click()}
                    style={{
                      height: '150px',
                      border: '2px dashed #ccc',
                      cursor: 'pointer',
                      background: '#f9f9f9',
                      overflow: 'hidden',
                    }}
                  >
                    {formik.values.imgpath ? (
                      <img
                        src={formik.values.imgpath}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <div className="text-center text-muted small">
                        <FiUploadCloud size={24} />
                        <div>Click to upload</div>
                      </div>
                    )}
                    <input
                      id="clothImageInput"
                      name="imgpath"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="d-none"
                    />
                  </div>
                  {formik.touched.imgpath && formik.errors.imgpath && (
                    <div className="form-text text-danger small">{formik.errors.imgpath}</div>
                  )}
                </div>

                {/* Name Input */}
                <div className="mb-3">
                  <label className="form-label small">Cloth Type Name</label>
                  <input
                    type="text"
                    name="print_type_name"
                    className={`form-control form-control-sm ${
                      formik.touched.print_type_name && formik.errors.print_type_name ? 'is-invalid' : ''
                    }`}
                    placeholder="e.g. Front,Back Sublimation"
                    value={formik.values.print_type_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.print_type_name && formik.errors.print_type_name && (
                    <div className="invalid-feedback small">{formik.errors.print_type_name}</div>
                  )}
                </div>

                {/* Save Button */}
                <div className="d-grid">
                  <button type="submit" className="btn btn-sm btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FiSave className="me-1" />
                        {editIndex !== null ? 'Update' : 'Save'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="col-md-8">
          <div className="card shadow-sm">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Cloth Print Type List</h6>
            </div>
            <div className="card-body p-0">
              {listLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped mb-0">
                    <thead className="table-light small">
                      <tr>
                        <th style={{ width: '70px' }}>Image</th>
                        <th>Name</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center text-muted small py-3">
                            No cloth print types added yet.
                          </td>
                        </tr>
                      ) : (
                        items.map((item, index) => (
                          <tr key={index} className="align-middle small">
                            <td>
                              <img
                                src={getImageUrl(item.imgpath)}
                                alt="cloth"
                                className="rounded"
                                style={{ width: 50, height: 50, objectFit: 'contain' }}
                              />
                            </td>
                            <td>{item.print_type_name}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => handleEdit(index)}
                              >
                                <FiEdit />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>{
                                  showModal({
                                    title: "Confirm Deletion",
                                    message: "Are you sure you want to delete?",
                                    onConfirm: () => {
                                      handleDelete_data({
                                        item,showToast,setItems,setActionLoader
                                      })
                                    },
                                    confirmText: "Yes",
                                    cancelText: "No",
                                  });
                                }}
                              >
                                  {actionLoader?.deleteId === item.id ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <FiTrash2 />
                                  )}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClothType;
